import { useState, useEffect } from 'react';
import {
    PrimaryButton,
    Dialog,
    DialogFooter,
    DefaultButton,
    IconButton } from 'office-ui-fabric-react';

import './ReplaceDialog.css';
import { IAutoHealingReplacement, IReviewContent, IAutoHealingReplacementPattern } from '../Interface/View/IAutoHealingView';
import { validateReplaceText, validateInputText, validateGUID, checkURLFormatIsCorrect, validateKBID } from '../Utilities/CommonFunction'
import InputWithValidator from '../ValidatorComponents/InputWithValidator/InputWithValidator'
import CustomTabBar from '../Modules/CommonPageContent/CustomTabBar/CustomTabBar';
import { Stack } from '@fluentui/react';


interface IReplaceDialogProps {
    title: string;
    replaceContent: IAutoHealingReplacement;
    reviewContent: IReviewContent;
    showDialog: boolean;
    algorithmType: string;
    onReplaceDialogDismiss: () => void;
    onReplaceDialogApply: (replacement:IAutoHealingReplacement) => void;
}

const TEXT_CLAWER_TEXT_FORMAT_ERROR_MESSAGE = "Please ensure that the text is not empty and its length is between 3 and 100 characters."
const URL_FORMAT_ERROR_MESSAGE = "Invalid URL format. Please enter a valid URL or leave it empty if you want to delete the URL. Ensure that the URL begins with 'https:// | file:// | ftp:// | mailto:' and does not contain any special characters."
const BROKEN_LINK_TEXT_FORMAT_ERROR_MESSAGE = "Please ensure that the text is not empty and its length is less than 200 characters."
const KBID_FORMAT_ERROR_MESSAGE = "Please ensure that the KBID is not empty and consists of 4-12 digits in length."
const GUID_FORMAT_ERROR_MESSAGE = "Please ensure that GUID is not empty and format like: ee9ca73f-fffd-4da6-95a4-a5138b95a1ff."
// let selectedTabId = new ObservableValue<string>('');

const ReplaceDialog = (props: IReplaceDialogProps) => {
    const [tempReplacementList, setTempReplacementList] = useState<IAutoHealingReplacementPattern[]>([]);
    const [tempNavReplacementList, setTempNavReplacementList] = useState<IAutoHealingReplacementPattern[]>([]);
    const [tempMetaReplacementList, setTempMetaReplacementList] = useState<IAutoHealingReplacementPattern[]>([]);
    const [verifyInput, setVerifyInput] = useState({})
    const [tabGroupNames, setTabGroupNames] = useState<Set<string>>(new Set())
    const [selectedTabId, setSelectedTabId] = useState<string>('')

    const getFilterList = (replacementList) => {
        const list = JSON.parse(JSON.stringify(replacementList? replacementList:[]));
        return list.map(element => {
            element.ReplacementContext = element.ReplacementContext.trim();
            return element;
        });
    }

    useEffect(()=>{
        const tempTabGroupNames = new Set<string>();
        if(props.reviewContent?.NavReplacementList?.length > 0) {
            tempTabGroupNames.add('Navigation')
            setTempNavReplacementList(getFilterList(props.replaceContent?props.replaceContent.NavReplacementList:props.reviewContent?.NavReplacementList));
        }
        if(props.reviewContent?.ReplacementList?.length > 0) {
            tempTabGroupNames.add('Content')
            setTempReplacementList(getFilterList(props.replaceContent?props.replaceContent.ReplacementList:props.reviewContent?.ReplacementList));
        }
        if(props.reviewContent?.MetaReplacementList.length > 0) {
            tempTabGroupNames.add('Metadata')
            setTempMetaReplacementList(getFilterList(props.replaceContent?props.replaceContent.MetaReplacementList:props.reviewContent?.MetaReplacementList))
        }
        setTabGroupNames(tempTabGroupNames)
    },[props])

    const changeReplaceListByType = (type: string, index: number,changeProp: string,value: string) => {
        if(type === 'Navigation') {
            const updatedList = [...tempNavReplacementList];
            updatedList[index][changeProp] = value;
            setTempNavReplacementList(updatedList);
        }else if(type === 'Content'){
            const updatedList = [...tempReplacementList];
            updatedList[index][changeProp] = value;
            setTempReplacementList(updatedList);
        }else if(type === 'Metadata') {
            const updatedList = [...tempMetaReplacementList];
            updatedList[index][changeProp] = value;
            setTempMetaReplacementList(updatedList);
        }
    }

    const handleReplacementChange = (index: number, value: string, changeProp: string, hasError: boolean) => {
        const tabs = Array.from(tabGroupNames)
        if(tabs.length > 1) {
            changeReplaceListByType(selectedTabId, index, changeProp, value)
            setVerifyInput({
                ...verifyInput,
                [`${selectedTabId}_${index}_${changeProp}`]: hasError
            })
        }else {
            changeReplaceListByType(tabs[0], index, changeProp, value)
            setVerifyInput({
                ...verifyInput,
                [`${tabs[0]}_${index}_${changeProp}`]: hasError
            })
        }
    };

    const handleApply = () => {
        const hasValidError = Object.values(verifyInput).some(item => item === true)
        if(!hasValidError) {
            const { replaceContent, reviewContent } = props;
            const content = replaceContent? replaceContent: reviewContent
            let paras:IAutoHealingReplacement = {
                ReplacementOriginalText: content.ReplacementOriginalText,
                ReplacementChangedText: content.ReplacementChangedText,
                ReplacementNavOriginalText: content.ReplacementNavOriginalText,
                ReplacementNavChangedText: content.ReplacementNavChangedText,
                MetaReplacementOriginalText: content.MetaReplacementOriginalText,
                MetaReplacementChangedText: content.MetaReplacementChangedText,
                ReplacementList: tempReplacementList,
                NavReplacementList: tempNavReplacementList,
                MetaReplacementList: tempMetaReplacementList
            }
            props.onReplaceDialogApply(paras);
            props.onReplaceDialogDismiss();
        }
    };

    const handleCancel = () => {
        const replacementList = props.replaceContent?props.replaceContent.ReplacementList:props.reviewContent?.ReplacementList
        const navReplacementList = props.replaceContent?props.replaceContent.NavReplacementList:props.reviewContent?.NavReplacementList
        const metaReplacementList = props.replaceContent?props.replaceContent.MetaReplacementList:props.reviewContent?.MetaReplacementList
        setTempNavReplacementList(getFilterList(navReplacementList));
        setTempReplacementList(getFilterList(replacementList));
        setTempMetaReplacementList(getFilterList(metaReplacementList))
        setVerifyInput({});
        props.onReplaceDialogDismiss();
    };

    const revertText = (key: string, index: number, property: string) => {
        const types = Array.from(tabGroupNames)
        const currentType: string = types.length > 1 ? selectedTabId : types[0]
        let orgList = [];
        if(currentType === 'Navigation') {
            orgList = props.replaceContent?props.replaceContent.NavReplacementList:props.reviewContent?.NavReplacementList
        }
        if(currentType === 'Content') {
            orgList = props.replaceContent?props.replaceContent.ReplacementList:props.reviewContent?.ReplacementList
        }
        if(currentType === 'Metadata') {
            orgList = props.replaceContent?props.replaceContent.ReplacementList:props.reviewContent?.MetaReplacementList
        }
        orgList.forEach(item => {
            if(item.HashKey === key) {
                changeReplaceListByType(currentType, index, property, item[property])
                return
            }
        })
    }

    const getDynamicContent = (replacementPattern, index: number): React.ReactNode => {
        return (
            <>
                <tr>
                    <td>URI</td>
                    <td style={{ wordBreak: 'break-all' }}>{replacementPattern.BrokenLink}</td>
                    <td>
                        <div style={{display: 'flex'}}>
                            <InputWithValidator
                                validator={checkURLFormatIsCorrect}
                                placeholder='Enter replace URI'
                                errorMessage={URL_FORMAT_ERROR_MESSAGE}
                                inputValue={replacementPattern?.ReplacementBrokenLink}
                                onValueChange={(value, hasError)=>{ handleReplacementChange(index, value, 'ReplacementBrokenLink', hasError) }}
                            />
                            <IconButton
                                onClick={()=>{ revertText(replacementPattern.HashKey, index, 'ReplacementBrokenLink') }}
                                iconProps={{ iconName: 'RevToggleKey' }}
                                className='text_replace_revert_btn'
                                title="Revert"
                            />
                        </div>
                    </td>
                </tr>
            </>
        )
    }

    const getDynamicNavigation = (replacementPattern, index: number): React.ReactNode => {
        return (
            <>
                <tr>
                    <td>GUID</td>
                    <td>{replacementPattern.BrokenLinkGuid}</td>
                    <td>
                        <div style={{display: 'flex'}}>
                            <InputWithValidator
                                validator={validateGUID}
                                placeholder='Enter replace GUID'
                                errorMessage={GUID_FORMAT_ERROR_MESSAGE}
                                inputValue={replacementPattern?.ReplacementBrokenLinkGuid}
                                onValueChange={(value, hasError)=>{ handleReplacementChange(index, value, 'ReplacementBrokenLinkGuid', hasError) }}
                            />
                            <IconButton
                                onClick={()=>{ revertText(replacementPattern.HashKey, index, 'ReplacementBrokenLinkGuid') }}
                                iconProps={{ iconName: 'RevToggleKey' }}
                                className='text_replace_revert_btn'
                                title="Revert"
                            />
                        </div>
                    </td>
                </tr>
                <tr>
                    <td>KBID</td>
                    <td>{replacementPattern.BrokenLinkKBID}</td>
                    <td>
                        <div style={{display: 'flex'}}>
                            <InputWithValidator
                                validator={validateKBID}
                                placeholder='Enter replace KBID'
                                errorMessage={KBID_FORMAT_ERROR_MESSAGE}
                                inputValue={replacementPattern?.ReplacementBrokenLinkKBID}
                                onValueChange={(value, hasError)=>{ handleReplacementChange(index, value, 'ReplacementBrokenLinkKBID', hasError) }}
                            />
                            <IconButton
                                onClick={()=>{ revertText(replacementPattern.HashKey, index, 'ReplacementBrokenLinkKBID') }}
                                iconProps={{ iconName: 'RevToggleKey' }}
                                className='text_replace_revert_btn'
                                title="Revert"
                            />
                        </div>
                    </td>
                </tr>
            </>
        )
    }

    const buildBrokenLinkTable = (replacementList: IAutoHealingReplacementPattern[], type: string) => {
        return replacementList.map((replacementPattern, index) => (
            <div key={replacementPattern.HashKey}>
                {/* <Card className="flex-grow bolt-table-card replace-dialog-table" contentProps={{ contentPadding: false }}> */}
                <Stack tokens={{ childrenGap: 8 }} className="customCard">
                    <table className='autohealing-dialog-content-table'>
                        <thead>
                            <tr>
                                <th style={{ width: '6%' }}></th>
                                <th style={{ width: '47%' }}>Link</th>
                                <th style={{ width: '47%' }}>Replace with</th>
                            </tr>
                        </thead>
                        <tbody>
                            { type === 'Navigation'? getDynamicNavigation(replacementPattern,index): getDynamicContent(replacementPattern,index) }
                            <tr>
                                <td>Text</td>
                                <td>{replacementPattern.BrokenLinkText}</td>
                                <td>
                                    <div style={{display: 'flex'}}>
                                        <InputWithValidator
                                            validator={validateInputText}
                                            placeholder='Enter replace Text'
                                            errorMessage={BROKEN_LINK_TEXT_FORMAT_ERROR_MESSAGE}
                                            inputValue={replacementPattern?.ReplacementBrokenLinkText}
                                            onValueChange={(value, hasError)=>{ handleReplacementChange(index, value, 'ReplacementBrokenLinkText', hasError) }}
                                        />
                                        <IconButton
                                            onClick={()=>{ revertText(replacementPattern.HashKey, index, 'ReplacementBrokenLinkText') }}
                                            iconProps={{ iconName: 'RevToggleKey' }}
                                            className='text_replace_revert_btn'
                                            title="Revert"
                                        />
                                    </div>
                                </td>
                            </tr>
                            { type === 'Navigation'? 
                                <tr>
                                    <td>IsCategoryOnly</td>
                                    <td>{`${replacementPattern.IsCategoryOnly}`}</td>
                                </tr>: null 
                            }
                        </tbody>
                    </table>
                </Stack>
                <br />
            </div>
        ));
    }

    const buildTextClawerTable = (replacementList: IAutoHealingReplacementPattern[]) => {
        return (
            // <Card className="flex-grow bolt-table-card replace-dialog-table" contentProps={{ contentPadding: false }}>
            <Stack tokens={{ childrenGap: 8 }} className="customCard">
                <table className='autohealing-dialog-content-table'>
                    <thead>
                        <tr>
                            <th style={{ width: '20%' }}>Text</th>
                            <th style={{ width: '45%' }}>Context</th>
                            <th style={{ width: '35%' }}>Replace with</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            replacementList.map((replacementPattern, index) => (
                                <tr key={replacementPattern.HashKey}>
                                    <td>{replacementPattern.BrokenLinkText}</td>
                                    <td>{replacementPattern.Context}</td>
                                    <td>
                                        <div style={{display: 'flex'}}>
                                            <InputWithValidator
                                                validator={validateReplaceText}
                                                placeholder='Enter replace Text'
                                                errorMessage={TEXT_CLAWER_TEXT_FORMAT_ERROR_MESSAGE}
                                                inputValue={replacementPattern?.ReplacementBrokenLinkText}
                                                onValueChange={(value, hasError)=>{ handleReplacementChange(index, value, 'ReplacementBrokenLinkText', hasError) }}
                                            />
                                            <IconButton
                                                onClick={()=>{ revertText(replacementPattern.HashKey, index, 'ReplacementBrokenLinkText') }}
                                                iconProps={{ iconName: 'RevToggleKey' }}
                                                className='text_replace_revert_btn'
                                                title="Revert"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </Stack>
        )
    }

    const renderTableByAlgorithm = (replacementList:IAutoHealingReplacementPattern[], type: string) => {
        if(props.algorithmType === 'TextClawer') {
            return buildTextClawerTable(replacementList)
        }
        if(props.algorithmType === 'BrokenLink') {
            return buildBrokenLinkTable(replacementList, type)
        }
    }

    const renderTable = (type: string) => {
        let replacementList = []
        if(type === 'Navigation') {
            replacementList = tempNavReplacementList
        }else if(type === 'Content') {
            replacementList = tempReplacementList
        }else {
            replacementList = tempMetaReplacementList
        }
        return renderTableByAlgorithm(replacementList, type)
    };

    return (
        <>
            {props.showDialog && (
                <Dialog
                    hidden={false}
                    modalProps={{
                        containerClassName: 'autohealing-dialog-container',
                        isBlocking: true
                    }}
                    dialogContentProps={{
                        title: props.title
                    }}
                    onDismiss={handleCancel}
                >
                    <div className="autohealing-dialog-content">
                        {
                            Array.from(tabGroupNames).length>1 ?
                            (
                                <>
                                    <CustomTabBar
                                        tabGroupNames={Array.from(tabGroupNames)}
                                        onSelectChange={ tabId => setSelectedTabId(tabId) }
                                    />
                                    {/* <Observer selectedTabId={selectedTabId}>
                                        {(props: { selectedTabId: string }) => {
                                            return renderTable(props.selectedTabId)
                                        }}
                                    </Observer> */}

                                    {
                                        renderTable(selectedTabId)
                                    }
                                </>
                            )
                            : renderTable(Array.from(tabGroupNames)[0])
                        }
                        
                    </div>
                    <DialogFooter>
                        <DefaultButton onClick={handleApply}>Apply</DefaultButton>
                        <PrimaryButton onClick={handleCancel}>Cancel</PrimaryButton>
                    </DialogFooter>
                </Dialog>
            )}
        </>
    );
};

export default ReplaceDialog;